// SPDX-License-Identifier: BUSL-1.1

/**
 * Filter bar for the flags management page.
 *
 * Provides provider, lifecycle, and search filters that the parent
 * page applies to the flags list before passing to DataTable.
 *
 */

import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface FlagFilterState {
  search: string;
  provider: string;
  lifecycle: string;
}

interface FlagFiltersProps {
  filters: FlagFilterState;
  onFiltersChange: (filters: FlagFilterState) => void;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const PROVIDER_OPTIONS = [
  { value: 'all', label: 'All Providers' },
  { value: 'internal', label: 'Internal' },
  { value: 'posthog', label: 'PostHog' },
  { value: 'launchdarkly', label: 'LaunchDarkly' },
  { value: 'env_var', label: 'Env Var' },
];

const LIFECYCLE_OPTIONS = [
  { value: 'all', label: 'All Stages' },
  { value: 'draft', label: 'Draft' },
  { value: 'rollout', label: 'Rollout' },
  { value: 'graduated', label: 'Graduated' },
  { value: 'sunset', label: 'Sunset' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function FlagFilters({ filters, onFiltersChange }: FlagFiltersProps) {
  const update = (patch: Partial<FlagFilterState>) =>
    onFiltersChange({ ...filters, ...patch });

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-neutral-500" />
        <Input
          placeholder="Search flags..."
          value={filters.search}
          onChange={(e) => update({ search: e.target.value })}
          className="pl-9"
        />
      </div>

      <Select
        value={filters.provider}
        onValueChange={(v) => v && update({ provider: v })}
      >
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Provider" />
        </SelectTrigger>
        <SelectContent>
          {PROVIDER_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.lifecycle}
        onValueChange={(v) => v && update({ lifecycle: v })}
      >
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Lifecycle" />
        </SelectTrigger>
        <SelectContent>
          {LIFECYCLE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
